/**
 * useCaseStageDropdown
 *
 * Fetch function for the case stage paginated dropdown, designed for use
 * with react-select-async-paginate + React Query caching.
 *
 * Data source: POST /global-dropdown?module=case_stage
 * Optionally filtered by practiceAreaId and isLeadSpecific.
 *
 * Caching strategy:
 *   Each unique combination of (practiceAreaId, isLeadSpecific, search, page, pageSize)
 *   is stored as a separate React Query entry. CaseStageSelect calls
 *   queryClient.fetchQuery() per page load, so:
 *   - First open: fetches from API, writes to cache.
 *   - Subsequent opens (within staleTime): served from cache instantly, no network request.
 *   - staleTime: 5 min  |  gcTime: 10 min
 */

import { getGlobalDropdown } from 'services/caseLeadDropdownService';

/**
 * Fetches one page of case stages from the global dropdown API.
 *
 * @param {Object}      params
 * @param {string|null} params.practiceAreaId  - Filter by practice area (optional)
 * @param {boolean}     params.isLeadSpecific  - Pass is_lead flag to API
 * @param {string}      params.search          - Keyword search string
 * @param {number}      params.page            - 1-based page number
 * @param {number}      params.pageSize        - Records per page
 * @returns {Promise<{ options: Array, hasMore: boolean, additional: { page: number } }>}
 */
export const fetchCaseStagesPage = async ({ practiceAreaId = null, isLeadSpecific = false, search = '', page = 1, pageSize = 15 }) => {
  const body = practiceAreaId ? { practice_area_id: [practiceAreaId] } : null;

  const response = await getGlobalDropdown(
    '',
    'case_stage',
    search,
    '',
    isLeadSpecific,
    page,
    pageSize,
    '',
    '',
    body,
    undefined,
    undefined,
    undefined,
    undefined,
    true
  );

  return {
    options: response?.data?.data || [],
    hasMore: response?.data?.has_next ?? false,
    additional: { page },
  };
};
